import { Bot, MessageCircle, X } from 'lucide-react';
import React from 'react';
import { Button } from './button';
import { CopilotChat, type CopilotChatRef } from './copilot-chat';

interface CopilotChatPopupProps {
  context?: {
    type: 'dashboard' | 'property';
    assetId?: string;
    assetName?: string;
  };
  trigger?: React.ReactNode;
  className?: string;
  showFloatingButton?: boolean;
}

export const CopilotChatPopup: React.FC<CopilotChatPopupProps> = ({
  context,
  trigger,
  className,
  showFloatingButton = true,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const chatRef = React.useRef<CopilotChatRef>(null);

  const defaultTrigger = (
    <Button
      variant="outline"
      size="sm"
      className="flex items-center gap-2"
      onClick={() => setIsOpen(true)}
    >
      <Bot className="h-4 w-4" />
      AI Assistant
    </Button>
  );

  return (
    <>
      {/* Optional Trigger Button */}
      {trigger && trigger}
      {!trigger && !showFloatingButton && defaultTrigger}

      {/* Floating Chat Bubble Button */}
      {showFloatingButton && (
        <div className="fixed bottom-4 right-4 z-40">
          <Button
            onClick={() => setIsOpen(true)}
            className="w-14 h-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 transition-all duration-200 hover:scale-110"
            size="icon"
          >
            <MessageCircle className="h-6 w-6 text-primary-foreground" />
          </Button>
        </div>
      )}

      {/* Floating Chat Widget */}
      {isOpen && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 pointer-events-auto"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            role="button"
            tabIndex={0}
          />

          {/* Chat Widget */}
          <div className="absolute bottom-4 right-4 w-96 h-[600px] bg-background border rounded-lg shadow-2xl flex flex-col pointer-events-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-muted/50 rounded-t-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">BetterCapital AI</h3>
                  <p className="text-xs text-muted-foreground">
                    Your financial assistant
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 p-0 hover:bg-background"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Chat Content */}
            <div className="flex-1 overflow-hidden">
              <CopilotChat
                ref={chatRef}
                context={context}
                className="h-full"
                hideHeader={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CopilotChatPopup;
